import styled from 'styled-components';
import { FlexCol, FlexRow } from 'components/primitives/flex';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Text } from 'components/primitives/typography';
import DraggableScroll from 'components/ui/DraggableScroll/DraggableScroll';
import { mediaMSmMax } from 'helpers/breakpoints';

export interface FilterChipItem {
  id: string;
  label: string;
  value: any;
  selected?: boolean;
  disabled?: boolean;
}

export interface FilterChipCarouselProps {
  title?: string;
  items: FilterChipItem[];
  onItemClick: (item: FilterChipItem, index: number) => void;
  isVisible?: boolean;
  isLoading?: boolean;
  mapVisible?: boolean;
  allowMultipleSelection?: boolean;
  className?: string;
  testIdPrefix?: string;
  refreshTrigger?: any;
  chipVariant?: 'default' | 'compact';
  titleVariant?: 'default' | 'large';
  [key: string]: any; // For styled-system props
  observeElement?: (id: string, element: HTMLElement) => void;
  unobserveElement?: (id: string) => void;
}

const FilterChipCarousel = forwardRef<HTMLDivElement, FilterChipCarouselProps>(
  (
    {
      title,
      items = [],
      onItemClick,
      isVisible = true,
      isLoading = false,
      mapVisible = false,
      className,
      testIdPrefix = 'filter-chip',
      refreshTrigger,
      chipVariant = 'default',
      titleVariant = 'default',
      ...styledProps
    },
    ref
  ) => {
    const [refreshing, setRefreshing] = useState(false);
    const [titleStatic, setTitleStatic] = useState('');

    // Handle refresh trigger
    useEffect(() => {
      if (refreshTrigger !== undefined) {
        setRefreshing(!refreshing);
      }
    }, [refreshTrigger]);

    // Update static title when not loading
    useEffect(() => {
      if (isLoading || !title) return;
      setTitleStatic(title);
    }, [title, isLoading]);

    const visibleItems = useMemo(() => items.filter((item) => !item.disabled), [items]);

    const displayTitle = titleStatic || title;

    if (!isVisible || visibleItems.length === 0) {
      return null;
    }

    return (
      <CarouselContainer ref={ref} className={className} showing {...styledProps}>
        {displayTitle && (
          <Text
            data-testid={`${testIdPrefix}-heading`}
            fWeight="700"
            fSize={titleVariant === 'large' ? '18px' : '16px'}
            mdMin={{ fSize: titleVariant === 'large' ? '20px' : '18px' }}
          >
            {displayTitle}
          </Text>
        )}
        <DraggableScroll refreshing={refreshing} rootClass={`${testIdPrefix}-carousel`}>
          <ChipCarousel mapVisible={mapVisible}>
            {visibleItems.map((item, index) => (
              <FilterChip
                key={item.id}
                onClick={() => onItemClick(item, index)}
                selected={item.selected || false}
                disabled={item.disabled || false}
                variant={chipVariant}
                data-testid={`${testIdPrefix}-${index}`}
                data-visibility-id={item.id}
              >
                {item.label}
              </FilterChip>
            ))}
          </ChipCarousel>
        </DraggableScroll>
      </CarouselContainer>
    );
  }
);

const CarouselContainer = styled(FlexCol)<{ showing: boolean }>`
  display: ${({ showing }) => (showing ? 'flex' : 'none')};
  gap: 32px;
  margin-bottom: 18px;

  ${mediaMSmMax} {
    gap: 24px;
    /* Extend edge-to-edge on mobile by counteracting BoatsContainer padding */
    margin-left: -22px;
    margin-right: -22px;
    width: calc(100vw);
  }
`;

const ChipCarousel = styled(FlexRow)<{ mapVisible: boolean }>`
  overflow: hidden;
  ${mediaMSmMax} {
    padding-right: 22px;
  }
`;

const FilterChip = styled.div<{
  selected: boolean;
  disabled: boolean;
  variant: 'default' | 'compact';
  isLast?: boolean;
}>`
  padding: ${({ variant }) => (variant === 'compact' ? '8px' : '12px')};
  margin-right: ${({ variant }) => (variant === 'compact' ? '12px' : '14px')};
  border-radius: 6px;
  font-weight: 600;
  font-size: ${({ variant }) => (variant === 'compact' ? '12px' : '14px')};
  line-height: ${({ variant }) => (variant === 'compact' ? '14px' : '16.8px')};
  color: ${({ disabled }) => (disabled ? '#999' : 'white')};
  display: flex;
  align-items: center;
  min-width: ${({ variant }) => (variant === 'compact' ? '80px' : 'auto')};
  word-wrap: nowrap;
  min-height: ${({ variant }) => (variant === 'compact' ? '40px' : '48px')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background-color: ${({ selected }) => (selected ? '#17233C' : '#0751C2')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: all 0.2s ease-in-out;
  justify-content: center;
  white-space: nowrap;

  &:hover {
    ${({ disabled, selected }) =>
      !disabled &&
      !selected &&
      `
      background-color: #0a4195;
    `}
  }

  ${mediaMSmMax} {
    font-size: ${({ variant }) => (variant === 'compact' ? '12px' : '14px')};
    line-height: ${({ variant }) => (variant === 'compact' ? '12px' : '13.2px')};
    min-width: ${({ variant }) => variant === 'compact' && '60px'};
    min-height: ${({ variant }) => (variant === 'compact' ? '36px' : '48px')};
    max-height: ${({ variant }) => (variant === 'compact' ? '40px' : '48px')};
    margin-right: ${({ variant }) => (variant === 'compact' ? '8px' : '12px')};
    &:first-child {
      margin-left: 22px;
    }
    &:last-child {
      margin-right: 0px;
    }
  }
`;

export default FilterChipCarousel;
